import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AppointmentType, ScheduleType } from '../../types';
import { PatientTypeEnum } from '../../types';

export type PatientType = PatientTypeEnum | '';

export interface PatientDetails {
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  notes?: string;
  id?: string;
  external_resource_id?: string;
  birth_date?: string;
  paymentType?: 'insurance' | 'myself' | null;
  insuranceDetails?: {
    provider?: string;
    policyHolderName?: string;
    policyHolderDob?: string;
    memberId?: string;
  };
  smsConsent?: boolean;
}

export interface ExistingPatientMatch {
  id: string;
  type: 'patient' | 'patient_request';
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  birth_date: string;
}

export const resolvePatient = (
  patientDetails: PatientDetails,
  match: ExistingPatientMatch | null
): PatientDetails => {
  if (!match) return patientDetails;
  return {
    ...patientDetails,
    firstName: match.firstName,
    lastName: match.lastName,
    email: match.email,
    phone: match.phone || patientDetails.phone,
    birth_date: match.birth_date || patientDetails.birth_date,
    id: match.type === 'patient' ? match.id : patientDetails.id,
  };
};

export interface AppointmentTimePreference {
  preferred_date: string;
  start_time: string | null;
  end_time: string | null;
  priority: number;
  any_time?: boolean;
}

export const createEmptyPatientDetails = (): PatientDetails => ({
  firstName: '',
  lastName: '',
  phone: '',
  email: '',
  notes: '',
  birth_date: '',
  paymentType: null,
  insuranceDetails: {
    provider: '',
    policyHolderName: '',
    policyHolderDob: '',
    memberId: '',
  },
  smsConsent: false,
});

interface AppointmentState {
  treatment: AppointmentType | null;
  patientType: PatientType;
  selectedSlot: string | null;
  patientDetails: PatientDetails;
  existingPatientMatch: ExistingPatientMatch | null;
  selectedPmsOperatoryId?: string | null;
  selectedDurationMinutes?: number | null;
  selectedOperatories?: string[];
  request_id: string | null;
  patient_request_id: string | null;
  requestable_type: string | null;
  primetimeFilter: ScheduleType;
  preferredTimeSlots: AppointmentTimePreference[];
  viewMode: 'table' | 'list' | null;
  slotsFetchDurationOverride: number | null;
  isFirstSlotsFetch: boolean;
}

const initialState: AppointmentState = {
  treatment: null,
  patientType: '',
  selectedSlot: null,
  patientDetails: createEmptyPatientDetails(),
  existingPatientMatch: null,
  selectedPmsOperatoryId: null,
  selectedDurationMinutes: null,
  selectedOperatories: [],
  request_id: null,
  patient_request_id: null,
  requestable_type: null,
  primetimeFilter: 'evenings_and_weekends',
  preferredTimeSlots: [],
  viewMode: null,
  slotsFetchDurationOverride: null,
  isFirstSlotsFetch: true,
};

const appointmentSlice = createSlice({
  name: 'appointment',
  initialState,
  reducers: {
    setTreatment(state, action: PayloadAction<AppointmentType>) {
      state.treatment = action.payload;
      // Reset downstream selections when treatment changes
      state.selectedSlot = null;
      state.selectedPmsOperatoryId = null;
      // Recompute duration if patient type already chosen
      if (state.patientType && action.payload) {
        state.selectedDurationMinutes =
          state.patientType === 'new'
            ? action.payload.newPatientMinutes
            : action.payload.existingPatientMinutes;
      } else {
        state.selectedDurationMinutes = null;
      }
    },
    setRequestMetadata: (state, action: PayloadAction<{ request_id: string; requestable_id: string; requestable_type: string }>) => {
      state.request_id = action.payload.request_id;
      state.patient_request_id = action.payload.requestable_id;
      state.requestable_type = action.payload.requestable_type;
    },
    setExistingPatientMatch(state, action: PayloadAction<ExistingPatientMatch | null>) {
      state.existingPatientMatch = action.payload;
    },
    setPatientType(state, action: PayloadAction<PatientType>) {
      if (state.patientType !== action.payload) {
        state.patientDetails = createEmptyPatientDetails();
        state.existingPatientMatch = null;
        state.selectedSlot = null;
        state.selectedPmsOperatoryId = null;
        state.selectedOperatories = [];
        state.request_id = null;
        state.patient_request_id = null;
        state.requestable_type = null;
        state.preferredTimeSlots = [];
        state.slotsFetchDurationOverride = null;
        state.isFirstSlotsFetch = true;
      }
      state.patientType = action.payload;
      if (state.treatment) {
        state.selectedDurationMinutes =
          action.payload === 'new'
            ? state.treatment.newPatientMinutes
            : state.treatment.existingPatientMinutes;
      } else {
        state.selectedDurationMinutes = null;
      }
    },
    setSelectedSlot(state, action: PayloadAction<string | null>) {
      state.selectedSlot = action.payload;
    },
    setSelectedPmsOperatoryId(state, action: PayloadAction<string | null>) {
      state.selectedPmsOperatoryId = action.payload;
    },
    setSelectedOperatories(state, action: PayloadAction<string[]>) {
      state.selectedOperatories = action.payload
    },
    setPatientDetails(state, action: PayloadAction<PatientDetails>) {
      state.patientDetails = action.payload;
    },
    setPrimetimeFilter(state, action: PayloadAction<ScheduleType>) {
      state.primetimeFilter = action.payload;
      state.selectedSlot = null; // Clear selected slot when filter changes
    },
    setPreferredTimeSlots(state, action: PayloadAction<AppointmentTimePreference[]>) {
      state.preferredTimeSlots = action.payload;
    },
    setViewMode(state, action: PayloadAction<'table' | 'list' | null>) {
      state.viewMode = action.payload;
    },
    setSlotsFetchDurationOverride(state, action: PayloadAction<number | null>) {
      state.slotsFetchDurationOverride = action.payload;
    },
    setIsFirstSlotsFetch(state, action: PayloadAction<boolean>) {
      state.isFirstSlotsFetch = action.payload;
    },
    resetAppointment(state) {
      state.treatment = null;
      state.patientType = '';
      state.selectedSlot = null;
      state.patientDetails = createEmptyPatientDetails();
      state.existingPatientMatch = null;
      state.selectedPmsOperatoryId = null;
      state.selectedDurationMinutes = null;
      state.selectedOperatories = [];
      state.request_id = null;
      state.patient_request_id = null;
      state.requestable_type = null;
      state.primetimeFilter = 'evenings_and_weekends';
      state.preferredTimeSlots = [];
      state.viewMode = null;
      state.slotsFetchDurationOverride = null;
      state.isFirstSlotsFetch = true;
    },
  },
});

export const {
  setTreatment,
  setPatientType,
  setSelectedSlot,
  setSelectedPmsOperatoryId,
  setSelectedOperatories,
  setPatientDetails,
  setExistingPatientMatch,
  setPrimetimeFilter,
  setRequestMetadata,
  resetAppointment,
  setPreferredTimeSlots,
  setViewMode,
  setSlotsFetchDurationOverride,
  setIsFirstSlotsFetch,
} = appointmentSlice.actions;

export default appointmentSlice.reducer;





